/**
 * Centralized Parse.Cloud.run wrapper with invalid-session handling.
 * Prevents stale tokens from causing repeated 209 errors across the app.
 */
import Parse from './initializeParse'
import authService from './authService'
import { isCompanyBlockedCloudError, signalCompanyAccessRefusal } from './CompanyAccessService'
import { setAuthenticated, setUserData } from '../stores/UserStore'
import { isInvalidSessionError, SESSION_EXPIRED_MESSAGE } from '../utils/sessionErrors'

// Callers refused in the same burst share one handling and each learns whether the session was kept.
let handlingInvalidSession: Promise<boolean> | null = null
let lastInvalidSessionKept = false
let lastInvalidSessionAt = 0
const INVALID_SESSION_DEBOUNCE_MS = 2000
const SESSION_EXPIRED_EVENT = 'sessionExpired'
// SC-452: a forced sign-out brings up the sign-in screen only after the event
// has fired, so the notice waits here until that screen takes it.
let sessionExpiredNoticePending = false

/**
 * Returns true when there is an active Colyseus room WebSocket connection.
 * In that case we must NOT sign the user out on a stale Back4App token —
 * the Colyseus session is independent and the player must stay in the room.
 * Sign-out will happen naturally on the next page refresh or explicit logout.
 */
function isColyseusRoomActive(): boolean {
  try {
    if (typeof window === 'undefined') return false
    // window.networkInstance is set by the Phaser bootstrap (same ref used by
    // disconnectRoomBeforeSignOut.ts, so this is the canonical active-room check).
    const net = (window as unknown as { networkInstance?: { room?: unknown } }).networkInstance
    return !!(net?.room)
  } catch {
    return false
  }
}

/**
 * Whether the player is in a room right now. Two decisions rest on it: whether an account switch
 * in another tab may reload the page at once, and whether a refused session may sign the player
 * out. Not `loggedIn` alone: nothing ever resets it, so a player kicked back to the dashboard after
 * a failed reconnect (`roomJoined` false, ROOM_LEFT already emitted) would count as in a room for
 * good. The reload would wait for a ROOM_LEFT that is past, and a refused session would leave them
 * half signed out (no session behind a dashboard still showing the account). A bound Colyseus room
 * counts whatever `roomJoined` says, so a reconnect keeps the player where they are; so does a
 * joined room in the office, as joining another room unbinds the old one first.
 */
async function isPlayerInRoomNow(): Promise<boolean> {
  if (isColyseusRoomActive()) return true
  // Lazy: the store's import graph reaches back to this module.
  const { default: store } = await import('../stores')
  const { user, room } = store.getState()
  return user.loggedIn && room.roomJoined
}

export type HandleInvalidSessionOptions = {
  /**
   * The caller got the session from `authService`, which already fired `sessionExpired` when it
   * ended it. The notice is still queued for a sign-in screen that comes up later, but the event
   * is not sent a second time.
   */
  sessionExpiredAnnounced?: boolean
  /**
   * The session token the refused call sent. A 209 is a verdict on that token only. If the shared
   * storage now holds a different one, another tab replaced the session (a re-sign-in or a
   * password change there revoked this tab's token), and signing out would wipe that newer session.
   * Left out, the 209 is taken as a verdict on the session.
   */
  sentToken?: string
}

/** The session the SDK sends with the next cloud call, read before the call goes out. */
type SentSession = { token?: string; userId?: string }

function currentSession(): SentSession {
  try {
    const user = Parse.User.current()
    return { token: user?.getSessionToken() || undefined, userId: user?.id || undefined }
  } catch {
    // Not knowing the token only means the 209 is handled as before.
    return {}
  }
}

/**
 * Resolves to true when the session was kept: the newer token another tab stored was taken over (or
 * a reload for another account is on its way), so nothing was reset. False when the app was reset.
 */
async function handleInvalidSession(options: HandleInvalidSessionOptions = {}): Promise<boolean> {
  if (handlingInvalidSession) return handlingInvalidSession
  const now = Date.now()
  if (now - lastInvalidSessionAt < INVALID_SESSION_DEBOUNCE_MS) return lastInvalidSessionKept
  lastInvalidSessionAt = now
  const run = resolveInvalidSession(options)
    .then(
      (kept) => (lastInvalidSessionKept = kept),
      (error) => {
        lastInvalidSessionKept = false
        throw error
      }
    )
    .finally(() => {
      handlingInvalidSession = null
    })
  handlingInvalidSession = run
  return run
}

async function resolveInvalidSession(options: HandleInvalidSessionOptions): Promise<boolean> {
  let sessionExpiredAnnounced = options.sessionExpiredAnnounced
  const storedToken = options.sentToken ? authService.readStoredToken() : undefined
  if (storedToken && storedToken !== options.sentToken) {
    // The light check takes over the newer token, or reloads when the account changed. A refusal
    // is real evidence that the stored session differs, so it skips the minute that rations plain
    // tab returns. No "session expired" here: this session did not expire.
    if (await authService.checkSessionLight({ force: true })) return true
    // It found the session gone too (the storage was emptied meanwhile, or the newer token is
    // refused as well) and announced that itself. The app is reset below like any ended session.
    sessionExpiredAnnounced = true
  }

  // Lazy: the store's import graph reaches back to this module.
  const { default: store } = await import('../stores')
  // A player in the office stays: a sign-out then would put them back in the office with no
  // user. One kicked back to the dashboard is not in a room and is reset like anyone else.
  const inRoom = await isPlayerInRoomNow()
  // Before the event, so a sign-in screen already open takes the notice live and
  // none is left over for a later one. In a room nobody is signed out, so no
  // sign-in screen should bring the notice up later.
  if (!inRoom) sessionExpiredNoticePending = true

  if (typeof window !== 'undefined' && !sessionExpiredAnnounced) {
    window.dispatchEvent(
      new CustomEvent(SESSION_EXPIRED_EVENT, {
        detail: { message: SESSION_EXPIRED_MESSAGE },
      })
    )
  }

  // Do NOT sign the player out while they are in an active Colyseus room.
  // The Back4App session token and the Colyseus WebSocket are independent:
  // a stale Parse token should never disconnect a player from their room mid-session.
  // The sign-out (and room leave) will happen naturally on the next page refresh
  // or when the user explicitly logs out.
  if (inRoom) {
    console.warn(
      '[parseCloud] Back4App session token expired while Colyseus room is active — ' +
        'skipping sign-out to preserve room session. Player will be signed out on next refresh.'
    )
    return false
  }

  try {
    await authService.signOut()
  } finally {
    // SC-452: signOut only clears Parse storage. Without this the app still
    // thinks someone is signed in and never shows the sign-in screen. Cleared
    // even when signOut fails, as the menu's Log out does.
    store.dispatch(setUserData(null))
    store.dispatch(setAuthenticated(false))
  }
  return false
}

/**
 * Show the "session expired" notice on the sign-in screen: once for each
 * forced sign-out, whether the screen was already open or came up after it,
 * and every time another part of the app announces an expired session.
 * Returns the unsubscribe.
 */
function subscribeSessionExpiredNotice(show: () => void): () => void {
  if (sessionExpiredNoticePending) {
    sessionExpiredNoticePending = false
    show()
  }
  const onExpired = () => {
    sessionExpiredNoticePending = false
    show()
  }
  window.addEventListener(SESSION_EXPIRED_EVENT, onExpired)
  return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired)
}

export type ParseCloudRunOptions = {
  /** When true (default), sign out and dispatch sessionExpired on 209/206. */
  handleInvalidSession?: boolean
}

/**
 * The refused call went with `sent`; the SDK now holds another token of the same account, which the
 * light check verified with Parse. Not for an account switch (no `become` ran, or the SDK user
 * differs) and not after a sign-out (no token left): then nothing is sent again.
 */
function tookOverNewerToken(sent: SentSession | undefined): boolean {
  if (!sent?.token || !sent.userId) return false
  const now = currentSession()
  return !!now.token && now.token !== sent.token && now.userId === sent.userId
}

/**
 * The cloud refuses a closed company's calls with 119 and the server's fixed text (SC-409). The
 * caller still gets the error to show; the access re-check brings up the disabled screen if the
 * company was suspended since the last check.
 */
function signalIfCompanyBlocked(error: unknown): void {
  if (isCompanyBlockedCloudError(error)) {
    signalCompanyAccessRefusal()
  }
}

/**
 * Run a cloud function with optional invalid-session recovery.
 *
 * A call refused only because another tab replaced the session (same account, newer token) is sent
 * once more with the token the check took over, so the caller never sees that 209. A 209 is raised
 * before the function runs, so repeating a write is safe. The retry is not itself recovered: a
 * second refusal goes to the caller.
 */
export async function parseCloudRun<T = unknown>(
  name: string,
  params?: Record<string, unknown>,
  options: ParseCloudRunOptions = {}
): Promise<T> {
  const { handleInvalidSession: shouldHandle = true } = options
  // Read before the call: the SDK may switch to a newer token while it is out, and the 209 is about
  // the one it went with.
  const sent = shouldHandle ? currentSession() : undefined
  try {
    return (await Parse.Cloud.run(name, params)) as T
  } catch (error) {
    if (shouldHandle && isInvalidSessionError(error)) {
      const kept = await handleInvalidSession({ sentToken: sent?.token })
      if (kept && tookOverNewerToken(sent)) {
        try {
          return (await Parse.Cloud.run(name, params)) as T
        } catch (retryError) {
          signalIfCompanyBlocked(retryError)
          throw retryError
        }
      }
    }
    signalIfCompanyBlocked(error)
    throw error
  }
}

export {
  isInvalidSessionError,
  handleInvalidSession,
  isColyseusRoomActive,
  isPlayerInRoomNow,
  subscribeSessionExpiredNotice,
}
